import React from 'react';

import autoBind from 'react-autobind';
import { If, Then } from 'react-if'
import Switch, { Case } from 'react-switch-case';
import DeckGL from "deck.gl";
import {WebMercatorViewport, FlyToInterpolator} from 'deck.gl';
import {GeoJsonLayer, IconLayer, TextLayer, ScatterplotLayer} from 'deck.gl'
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { EditableGeoJsonLayer } from "@deck.gl-community/editable-layers";
import {
  TranslateMode,
  ModifyMode,
  CompositeMode,
} from '@deck.gl-community/editable-layers';


// import other components within the same project here
import Config from "../Config.jsx";

import Localization from "../Localization.jsx"
import { isUnsetCoord } from "../utils/hotspotGeo.js";

import RootPage from "../pages/RootPage.jsx"
import HotspotListView from "../views/hotspot/HotspotListView.jsx"
import HotspotViewerBar from "../views/hotspot/HotspotViewerBar.jsx"
import HotspotDetailView from "../views/hotspot/HotspotDetailView.jsx"
import ThreeDJobIndicator from "../views/hotspot/ThreeDJobIndicator.jsx"
import ThreeDJobManager from "../tools/ThreeDJobManager.js"

import { Button, SimpleGrid, ActionIcon } from '@mantine/core';
import VerticalZoomSlider from "../views/VerticalZoomSlider.jsx";
import { IconPlus, IconMinus } from '@tabler/icons-react';
import { BRAND_PRIMARY } from "../theme/brandTheme.js";

import MarkerWithLabel from "react-google-maps/lib/components/addons/MarkerWithLabel"

const VIEW_HOTSPOT_LIST = 0;
const VIEW_HOTSPOT_DETAIL = 1;

const DEFAULT_MAP_ZOOM = 15;

class HotspotViewerPage extends React.Component {


  static VIEW_HOTSPOT_LIST() {
    return VIEW_HOTSPOT_LIST;
  }

  static VIEW_HOTSPOT_DETAIL() {
    return VIEW_HOTSPOT_DETAIL;
  }

  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // Add edit modes for hotspot dragging
    this.editModes = new CompositeMode([new TranslateMode()]);

    // state variables here
    this.state = {
      localeColor: BRAND_PRIMARY,
      hotspotList: [],
      // Three-state list rendering: an in-flight fetch must not render as the
      // "this locale has no hotspot" reminder. Starts true because the
      // constructor fires getHotspotByLocaleID() below.
      hotspotListLoading: true,
      localeCenter: { lat: parseFloat(this.props.currentLocale.mid_lat), lng: parseFloat(this.props.currentLocale.mid_lon) },
      localeBound: {
        ul_lat: parseFloat(this.props.currentLocale.ul_lat),
        ul_lon: parseFloat(this.props.currentLocale.ul_lon),
        br_lat: parseFloat(this.props.currentLocale.br_lat),
        br_lon: parseFloat(this.props.currentLocale.br_lon),
      },
      new_hotspot: true,
      zoom: DEFAULT_MAP_ZOOM,
      currentView: VIEW_HOTSPOT_LIST,
      currentHotspot: {},
      hotspotCenter: null,
      map: {
        zoom: 0,
        center: {
          lat: 0,
          lon: 0
        },
        bounds:{
          ul:{
            lat: 0,
            lon: 0
          },
          br:{
            lat: 0,
            lon: 0
          }
        },
        style: Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon,
        _style: "cartoon",
        lang: "zh"
      },
      viewState:{
        latitude: 37.7751,
        longitude: -122.4193,
        zoom: 11,
        bearing: 0,
        pitch: 0,
        width: 500,
        height: 500
      },
      hotspotID: -1,
      width: 0,
      height: 0,
      newHotspotID: -1,
      flyContainer: new FlyToInterpolator({speed: 2}),
      marker_data: {
        show: false,
        out_screen: false,
        lat: 0,
        lon: 0,
        meta: null,
        index: -1
      },
      // Add edit mode state
      isHotspotEditMode: false,
      editableHotspotData: null,
    };
    autoBind(this);
    this.onHotspotEdit = this.onHotspotEdit.bind(this);

    // getHotspotByLocaleID() runs from here, before mount, so
    // setHotspotListLoading has to know not to call setState yet.
    this.isComponentMounted = false;
    this.getHotspotByLocaleID(this.props.currentLocale.id);
    this.state.hotspotCenter = this.state.localeCenter;
    this.state.viewState = {
      longitude: this.state.currentView === VIEW_HOTSPOT_LIST? this.state.localeCenter.lng : this.state.hotspotCenter.lng,
      latitude: this.state.currentView === VIEW_HOTSPOT_LIST? this.state.localeCenter.lat : this.state.hotspotCenter.lat,
      zoom: this.state.zoom,
      bearing: 0,
      pitch: 0,
      width: 500,
      height: 500,
      maxZoom: 17,
      minZoom: 3
    };
  }

  componentDidMount() {
    this.isComponentMounted = true;
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
    this.props.root.setTitle("")
    this.syncMapLanguage();
  }

  componentDidUpdate() {
    this.syncMapLanguage();
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  // Mirrors the direct-assign + setState idiom used across this page, but stays
  // quiet before mount because the first hotspot request is fired from the
  // constructor.
  setHotspotListLoading(loading) {
    this.state.hotspotListLoading = loading;
    if (this.isComponentMounted) {
      this.setState({ hotspotListLoading: loading });
    }
  }

  // Keep the map style in sync with the current locale language.
  // Runs after render (componentDidMount/componentDidUpdate) so that we never
  // call setState during render. The guard prevents a setState loop: once
  // map.lang matches the current language, this is a no-op.
  syncMapLanguage() {
    if (Localization.strings.getLanguage() == this.state.map.lang) {
      return;
    }
    let newStyle;
    switch (this.state.map._style) {
      case "mono":
        newStyle = Config.MBOX_STYLE(Localization.strings.getLanguage()).mono;
        break;
      case "satellite":
        newStyle = Config.MBOX_STYLE(Localization.strings.getLanguage()).satellite;
        break;
      case "cartoon":
        newStyle = Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon;
        break;
      default:
        newStyle = this.state.map.style;
        break;
    }
    this.setState({
      map: {
        ...this.state.map,
        lang: Localization.strings.getLanguage(),
        style: newStyle
      }
    });
  }

  zoomIn = () => {
    let newZoom = Math.round(this.state.viewState.zoom) + 1;
    if (newZoom > this.state.viewState.maxZoom) {
      newZoom = this.state.viewState.maxZoom;
    }
    this.setState({
      viewState: {
        ...this.state.viewState,
        zoom: newZoom
      }
    });
  }

  zoomOut = () => {
    let newZoom = Math.round(this.state.viewState.zoom) - 1;
    if (newZoom < this.state.viewState.minZoom) {
      newZoom = this.state.viewState.minZoom;
    }
    this.setState({
      viewState: {
        ...this.state.viewState,
        zoom: newZoom
      }
    });
  }

  updateWindowDimensions() {
    // Calculate available height for the map
    // Total viewport height minus TopBar (64px) minus HotspotViewerBar height
    let barHeight = 56; // default estimate
    if (this.refs.hotspotViewerBar && this.refs.hotspotViewerBar.refs.div) {
      barHeight = this.refs.hotspotViewerBar.refs.div.getBoundingClientRect().height;
    }
    const topBarHeight = 64;
    const height = window.innerHeight - topBarHeight - barHeight;

    this.setState({
      width: window.innerWidth,
      height: height > 0 ? height : 400, // fallback minimum height
    });
    this.state.width = window.innerWidth;
    this.state.height = height > 0 ? height : 400;
  }

  changeView(view) {
    this.setState({ currentView: view });
    this.state.currentView = view;

    // Enable/disable hotspot edit mode based on view
    if (view === VIEW_HOTSPOT_DETAIL) {
      this.setState({ isHotspotEditMode: true });
      this.state.isHotspotEditMode = true;
      this.updateEditableHotspotData();
    } else {
      this.setState({ isHotspotEditMode: false });
      this.state.isHotspotEditMode = false;
      this.setState({ editableHotspotData: null });
      this.state.editableHotspotData = null;
    }

    if (this.state.currentView == VIEW_HOTSPOT_LIST) {
      this.setState({
        //currentHotspot: {},
        zoom: DEFAULT_MAP_ZOOM,
      });
      //this.state.currentHotspot = {};
      this.state.zoom = DEFAULT_MAP_ZOOM;
      this.currentHotspot = null;
    }
    this.props.root.setTitle("")
  }

  getHotspotByLocaleID(localeID) {
    this.state.hotspotList = [];
    this.setHotspotListLoading(true);
    let requestObj = {
      table: "hotspot",
      filter: {
        epack_id: localeID
      },
      order_by: "name",
      order_mode: "ASC",
    };
    this.props.root.refs.API.request("get_list", requestObj, this.getHotspotOK, this.getHotspotFail);
  }

  getHotspotOK(res) {
    const list = Array.isArray(res && res.body && res.body.result) ? res.body.result : [];
    this.setHotspotListLoading(false);
    this.setState({hotspotList: list});
    this.state.hotspotList = list;

    for (let i = 0; i < this.state.hotspotList.length; i++) {
      if (this.state.hotspotList[i].id == this.state.hotspotID) {
        this.setState({ currentHotspot: this.state.hotspotList[i] });
        this.state.currentHotspot = this.state.hotspotList[i];
      }

      if (this.state.hotspotList[i].id == this.state.newHotspotID) {
        this.setState({ newHotspotID: -1 });
        this.state.newHotspotID = -1;

        this.handleHotspotClicked(this.state.hotspotList[i]);
      }
    }
  }

  getHotspotFail(err) {
    // Known-to-be-nothing rather than not-known-yet, so leave the loading state.
    this.setHotspotListLoading(false);
    console.log("get hotspot from server failed");
  }

  addHotspot() {
    //check if hotspot number is more than max 
    var max_len = this.props.root.refs.userDataProfile.state.userInfo.role == 2?Config.MAX_HOTSPOT_LEN().PREMIUM:Config.MAX_HOTSPOT_LEN().NORMAL;
    if(this.state.hotspotList.length >= max_len){
      // pop up error message
      this.props.root.addNotification(Localization.strings.EXCEED_MAX_HOTSPOT, false);
    }else{
      console.log('on add hotspot clicked');
      let defaultHotspotColor = BRAND_PRIMARY;
      let requestObj = {
        table: "hotspot",
        row: {
          name: Localization.strings.NEW_HOTSPOT,
          epack_id: this.props.currentLocale.id,
          color: parseInt(defaultHotspotColor.substring(1), 16),
          radius: 30
        }
      };
      this.props.root.refs.API.request("insert", requestObj, this.addHotspotOK, this.addHotspotFail);
    }
  }

  addHotspotOK(res) {
    let id = res.body['result'];
    this.setState({ 
      newHotspotID: id,
      new_hotspot: true
    });
    this.state.newHotspotID = id;
    this.state.new_hotspot = true;

    this.getHotspotByLocaleID(this.props.currentLocale.id);

    var requestObj = {epack_id: this.props.currentLocale.id};
    this.props.root.refs.API.request("touch_epack", requestObj);
  }

  addHotspotFail(err) {
    console.log("add hotspot fail");
  }

  handleHotspotClicked(hotspot, changeHistory=true) {
    let zoom = this.state.zoom;
    if (this.state.map != null) {
      zoom = this.state.map.zoom;
    }
    this.setState({ currentHotspot: hotspot });
    this.state.currentHotspot = hotspot;
    this.setState({ hotspotID: hotspot.id });
    this.state.hotspotID = hotspot.id;
    let center = this.state.localeCenter;
    if (hotspot['lat'] != "0" && hotspot['lon'] != "0") {
      center = {lat: parseFloat(hotspot['lat']), lng: parseFloat(hotspot['lon']) };
      this.setState({
        marker_data:{
          ...this.state.marker_data,
          show: true,
          lat: parseFloat(hotspot.lat),
          lon: parseFloat(hotspot.lon)
        },
        new_hotspot: false
      });
    }else{
      this.setHotspotPosition();
      this.setState({
        marker_data:{
          ...this.state.marker_data,
          show: true,
          lat: parseFloat(center.lat),
          lon: parseFloat(center.lng)
        }
      });
    }

    //todo: change viewstate too
    this.mapPanTo(center);

    this.changeView(VIEW_HOTSPOT_DETAIL);
    if (changeHistory) {
      this.props.root.pushHistory("hotspot/detail");
    }
  }

  // Jump from the map's 3D-job indicator back into the job's component: select
  // the hotspot (opens the detail view) and, once the detail view has mounted
  // and loaded its content list, open that component's edit dialog.
  open3DComponentPanel(job) {
    if (!job) return;
    const hotspot = (this.state.hotspotList || []).find(
      (h) => h && parseInt(h.id) === parseInt(job.hotspotId)
    );
    if (!hotspot) {
      this.props.root.addNotification(Localization.strings.MODEL_3D_NO_AVAILABLE || 'Hotspot not found.', false);
      return;
    }
    this.handleHotspotClicked(hotspot);

    // The detail view mounts on the next render and fetches its content list;
    // openContentById buffers the request until the list arrives.
    const tryOpen = (attempt) => {
      const detail = this.refs.hotspotDetailView;
      if (detail && typeof detail.openContentById === 'function') {
        detail.openContentById(job.componentId);
      } else if (attempt < 20) {
        setTimeout(() => tryOpen(attempt + 1), 150);
      }
    };
    tryOpen(0);
  }

  deleteHotspotClicked(id) {
    this.setState({ hotspotID: id });
    this.state.hotspotID = id;
    let dialogText = {
      title: Localization.strings.DELETE_HOTSPOT,
      content: Localization.strings.DELETE_REMINDER,
      confirm: Localization.strings.DELETE,
    };
    this.props.root.refs.confirmDialog.show(dialogText, this.deleteHotspotConfirmClicked, this.deleteHotspotCancelClicked);
  }

  deleteHotspotConfirmClicked() {
    if (this.state.hotspotID >= 0) {
      // The server cascades the hotspot's components; mirror that for any 3D
      // generations running on them (remote cancels + cron rows + registry),
      // and do it BEFORE the delete so the bookkeeping still authorizes.
      ThreeDJobManager.handleHotspotDeleted(this.state.hotspotID);
      let requestObj = {table: "hotspot", filter: {id: this.state.hotspotID}};
      this.props.root.refs.API.request("delete", requestObj, this.deleteHotspotOK, this.deleteHotspotFail);
    }
    this.setState({ hotspotID: -1 });
    this.state.hotspotID = -1;
  }

  deleteHotspotCancelClicked() {
    this.setState({ hotspotID: -1 });
    this.state.hotspotID = -1;
  }

  deleteHotspotOK(res) {
    this.getHotspotByLocaleID(this.props.currentLocale.id);

    var requestObj = {epack_id: this.props.currentLocale.id};
    this.props.root.refs.API.request("touch_epack", requestObj);
  }

  deleteHotspotFail(err) {
    console.log("delete hotspot fail " + err);
  }

  handleBackButtonClicked() {
    if (this.state.currentView == VIEW_HOTSPOT_LIST) {
      this.props.root.changeStatus(this.props.root.STATUS_VIEW_LOCALE());
    }
    else if (this.state.currentView == VIEW_HOTSPOT_DETAIL) {
      //check empty content

      this.changeView(VIEW_HOTSPOT_LIST);
    }
    let position = {
      lat: this.state.localeCenter.lat,
      lng: this.state.localeCenter.lng
    };
    this.mapPanTo(position, false);

    // update marker data
    this.setState({
      marker_data:{
        ...this.state.marker_data,
        show: false,
        lat: 0,
        lon: 0
      }
    });
    this.state.marker_data = {
      ...this.state.marker_data,
      show: false,
      lat: 0,
      lon: 0
    }
  }

  handleForwardButtonClicked() {
    if (this.state.currentView == VIEW_HOTSPOT_LIST) {
      this.handleHotspotClicked(this.state.currentHotspot, false);
    }
  }

  hotspotMarkerClicked(index) {
    this.handleHotspotClicked(this.state.hotspotList[index]);
  }

  teleport() {
    if (this.state.currentHotspot) {
      let position = {
        lat: parseFloat(this.state.currentHotspot.lat),
        lng: parseFloat(this.state.currentHotspot.lon)
      };
      this.mapPanTo(position);
    }
  }

  mapPanTo(position, to_hotspot = true) {
    this.setState({
      viewState: {
        ...this.state.viewState,
        longitude: position.lng,
        latitude: position.lat,
        zoom: to_hotspot? 17: 15,
        transitionDuration: 1000,
        transitionInterpolator: this.state.flyContainer
      }
    });
  }

  hotspotListEmpty(){
    return this.state.hotspotList.length == 0;
  }

  hotspotEditValidation(){
    let result = {
      flag: 0, //0: ok, 1: prompt, 2: warning
      message: ""
    }

    
    if (this.refs.hotspotDetailView.state.hotspotName == ""){
      result.flag = 2;
      result.message = Localization.strings.HOTSPOT_EMPTY_NAME_REMINDER;
    }else if (this.state.new_hotspot && this.refs.hotspotDetailView.state.contentList.length == 0){
      result.flag = 1;
      result.message = Localization.strings.HOTSPOT_EDIT_REMINDER_1;
    }else if (this.state.new_hotspot){
      result.flag = 1;
      result.message = Localization.strings.HOTSPOT_EDIT_REMINDER_2;
    }else if (this.refs.hotspotDetailView.state.contentList.length == 0){
      result.flag = 1;
      result.message = Localization.strings.EMPTY_TAG_PROMPT;
    }
    return result;
  }

  updateHotspotDetail() {
    this.updateMapState();
    let hotspotDetailViewState = this.refs.hotspotDetailView.state;
    let requestObj = {
      table: "hotspot",
      where_field: "id",
      where_value: this.state.currentHotspot.id,
      row: {
        name: hotspotDetailViewState.hotspotName,
        color: parseInt(hotspotDetailViewState.hotspotColor.substring(1), 16),
        radius: hotspotDetailViewState.hotspotRadius,
        disclose: hotspotDetailViewState.hotspotVisibility,
        lat: parseFloat(this.state.currentHotspot.lat),
        lon: parseFloat(this.state.currentHotspot.lon)
      }
    };
    this.props.root.refs.API.request("update", requestObj, this.updateHotspotDetailOK, this.updateHotspotDetailFail);
  }

  updateHotspotDetailOK(res) {
    this.getHotspotByLocaleID(this.props.currentLocale.id);
    this.props.root.addNotification(Localization.strings.UPDATED, true);

    var requestObj = {epack_id: this.props.currentLocale.id};
    this.props.root.refs.API.request("touch_epack", requestObj);
  }

  updateHotspotDetailFail(err) {
    console.log("update hotspot detail fail " + err);
    this.props.root.addNotification(Localization.strings.UPDATE_FAILED, true);
  }

  addContentClicked() {
    this.refs.hotspotDetailView.handleAddContentClicked();
  }
  
  onMapMounted = (map) => {
    this.state.map = map;
  }

  onMarkerMounted = (marker) => {
    this.state.currentHotspotMarker = marker;
  }

  hotspotMarkerDragEnd() {
    let position = {
      lat: this.state.marker_data.lat,
      lng: this.state.marker_data.lon
    }
    var bound = {
      ul:{
        lat: Math.min(this.state.map.bounds.ul.lat, this.state.localeBound.ul_lat),
        lon: Math.max(this.state.map.bounds.ul.lon, this.state.localeBound.ul_lon)
      },
      br:{
        lat: Math.max(this.state.map.bounds.br.lat, this.state.localeBound.br_lat),
        lon: Math.min(this.state.map.bounds.br.lon, this.state.localeBound.br_lon)
      }
    };
    position.lat = Math.max(bound.br.lat, Math.min(position.lat, bound.ul.lat));
    position.lng = Math.max(bound.ul.lon, Math.min(position.lng, bound.br.lon));

    this.updateMapState();

    this.state.currentHotspot.lat = position.lat;
    this.state.currentHotspot.lon = position.lng;

    // update marker data
    this.setState({
      marker_data:{
        ...this.state.marker_data,
        lat: this.state.currentHotspot.lat,
        lon: this.state.currentHotspot.lon
      }
    });
    this.state.marker_data = {
      ...this.state.marker_data,
      lat: this.state.currentHotspot.lat,
      lon: this.state.currentHotspot.lon
    }
  }

  updateHotspotColor (color) {
    this.updateMapState();

    let hotspot = this.state.currentHotspot;
    hotspot.color = parseInt(color.hex.substring(1), 16);
    this.setState({ currentHotspot: hotspot });
    this.state.currentHotspot = hotspot;
    
    // Update editable data
    this.updateEditableHotspotData();
  }

  updateHotspotRadius(radius) {
    this.updateMapState();

    let hotspot = this.state.currentHotspot;
    hotspot.radius = radius;
    this.setState({ currentHotspot: hotspot });
    this.state.currentHotspot = hotspot;
    
    // Update editable data
    this.updateEditableHotspotData();
  }

  setHotspotPosition() {
    let requestObj = {
      table: "hotspot",
      where_field: "id",
      where_value: this.state.currentHotspot.id,
      row: {
        lat: this.state.localeCenter.lat,
        lon: this.state.localeCenter.lng,
      }
    };
    this.props.root.refs.API.request("update", requestObj, this.updateHotspotDetailOK, this.updateHotspotDetailFail);

    // show marker and update editable data
    this.setState({
      marker_data:{
        show: true,
        lat: this.state.localeCenter.lat,
        lon: this.state.localeCenter.lng,
        meta: null,
        index: -1
      }
    });
    
    // Update current hotspot and editable data
    this.state.currentHotspot.lat = this.state.localeCenter.lat;
    this.state.currentHotspot.lon = this.state.localeCenter.lng;
    this.updateEditableHotspotData();
  }

  // Add method to update editable hotspot data
  updateEditableHotspotData() {
    if (!this.state.currentHotspot || isUnsetCoord(this.state.currentHotspot.lat) || isUnsetCoord(this.state.currentHotspot.lon)) {
      this.setState({ editableHotspotData: null });
      this.state.editableHotspotData = null;
      return;
    }

    const hotspot = this.state.currentHotspot;
    const hotspotGeoJson = {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        properties: {
          hotspotId: hotspot.id,
          name: hotspot.name,
          color: hotspot.color,
          radius: hotspot.radius
        },
        geometry: {
          type: "Point",
          coordinates: [parseFloat(hotspot.lon), parseFloat(hotspot.lat)]
        }
      }]
    };

    this.setState({ editableHotspotData: hotspotGeoJson });
    this.state.editableHotspotData = hotspotGeoJson;
  }

  // Add method to handle hotspot editing
  onHotspotEdit({ updatedData, editType, editContext }) {
    if (editType === 'movePosition' || editType === 'finishMovePosition' || editType === 'translating' || editType === 'translated') {
      const newData = JSON.parse(JSON.stringify(updatedData));
      const coords = newData.features[0].geometry.coordinates;
      
      // Constrain to locale bounds
      const lat = Math.max(this.state.localeBound.br_lat, Math.min(coords[1], this.state.localeBound.ul_lat));
      const lon = Math.max(this.state.localeBound.ul_lon, Math.min(coords[0], this.state.localeBound.br_lon));
      
      // Update coordinates
      newData.features[0].geometry.coordinates = [lon, lat];
      
      // Update state
      this.setState({ editableHotspotData: newData });
      this.state.editableHotspotData = newData;
      
      // Update current hotspot
      this.state.currentHotspot.lat = lat;
      this.state.currentHotspot.lon = lon;
      this.setState({ currentHotspot: { ...this.state.currentHotspot } });
      
      // Update marker data
      this.setState({
        marker_data: {
          ...this.state.marker_data,
          lat: lat,
          lon: lon
        }
      });
      this.state.marker_data = {
        ...this.state.marker_data,
        lat: lat,
        lon: lon
      };
      
      // Update database if finished moving
      if (editType === 'finishMovePosition' || editType === 'translated') {
        this.hotspotMarkerDragEnd();
      }
    }
  }

  updateMapState() {
    let zoom = this.state.map.zoom;
    let center = {
      lat: this.state.map.center.lat,
      lng: this.state.map.center.lon
    };

    this.setState({
      zoom: zoom,
      hotspotCenter: center,
    });
    this.state.zoom = zoom;
    this.state.hotspotCenter = center;
  }

  getCurrentHotspotLatLon(){
    if (!this.state.currentHotspot){
      return;
    }
    return {
      lat: this.state.currentHotspot.lat,
      lng: this.state.currentHotspot.lon
    }
  }

  getCurrentLocaleName(){
    if (!this.props.currentLocale){
      return;
    }
    return this.props.currentLocale.name
  }

  getCurrentHotspotName(){
    if (!this.state.currentHotspot){
      return;
    }
    return this.state.currentHotspot.name
  }

  genBoxData(){
    if (!this.props.currentLocale){
      return;
    }
    var boxData = []
    boxData.push({
      'type': 'Feature',
      'color': parseInt(this.props.currentLocale.color).toString(16),
      'geometry': {
        'type': 'Polygon',
        'coordinates': [[
          [parseFloat(this.props.currentLocale.ul_lon), parseFloat(this.props.currentLocale.ul_lat)],
          [parseFloat(this.props.currentLocale.ul_lon), parseFloat(this.props.currentLocale.br_lat)],
          [parseFloat(this.props.currentLocale.br_lon), parseFloat(this.props.currentLocale.br_lat)],
          [parseFloat(this.props.currentLocale.br_lon), parseFloat(this.props.currentLocale.ul_lat)],
          [parseFloat(this.props.currentLocale.ul_lon), parseFloat(this.props.currentLocale.ul_lat)]
        ]]
      }
    });

    var boxLayers = new GeoJsonLayer({
        id: 'geojson-layer',
        data: boxData,
        lineWidthScale: 4,
        opacity: 0.4,
        filled: true,
        stroked: true,
        lineWidth: 2,
        lineWidthMinPixels: 2,
        wireframe: true,
        getLineColor: f => {
          var hex = f.color;
          while(hex.length != 6){
            hex = '0' + hex;
          }
          // convert to RGB
          return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0];
        },
        getFillColor: f => {
          var hex = f.color;
          while(hex.length != 6){
            hex = '0' + hex;
          }
          hex = hex + '00';
          // convert to RGB
          return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
        },
        pickable: true,
      });

      return boxLayers
  }

  genIconLayer(){
    var iconData = []
    for(var i = 0; i<this.props.parent.state.localeListInMap.length; i++){
      iconData.push({
        'color': parseInt(this.props.parent.state.localeListInMap[i].color).toString(16),
        'coordinates': [(parseFloat(this.props.parent.state.localeListInMap[i].ul_lon) + parseFloat(this.props.parent.state.localeListInMap[i].br_lon))/2, (parseFloat(this.props.parent.state.localeListInMap[i].ul_lat) + parseFloat(this.props.parent.state.localeListInMap[i].br_lat))/2],
        'meta': this.props.parent.state.localeListInMap[i]
      });
    }

    const ICON_MAPPING = {
      marker: {
        x: 0,
        y: 0,
        width: 128,
        height: 128,
        anchorY: 128,
        mask: true
      }
    };

    var iconLayers = new IconLayer({
      id: 'icon-layer',
      data: iconData,
      pickable: true,
      autoHighlight: false,
      iconAtlas: 'https://raw.githubusercontent.com/visgl/deck.gl-data/master/website/icon-atlas.png',
      iconMapping: ICON_MAPPING,
      getIcon: d => 'marker',

      sizeScale: 10,
      getPosition: d => d.coordinates,
      getSize: d => 5,
      getColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        // convert to RGB
        return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
      },
    });

    return iconLayers;
  }

  genTextLayer(){
    var textData = []
    var charSet = ''

    // todo: do not show text label when adding new hotspot

    for(var i = 0; i<this.state.hotspotList.length; i++){
      if(this.state.currentView === VIEW_HOTSPOT_LIST){
        textData.push({
          'color': parseInt(this.state.hotspotList[i].color).toString(16),
          'name': this.state.hotspotList[i].name,
          'coordinates': [parseFloat(this.state.hotspotList[i].lon), parseFloat(this.state.hotspotList[i].lat)]
        });
      }else{
        if(this.state.hotspotList[i].id == this.state.currentHotspot.id){
          // In edit mode, don't show text for current hotspot (handled by editable layer)
          if (!this.state.isHotspotEditMode) {
            textData.push({
              'color': parseInt(this.state.hotspotList[i].color).toString(16),
              'name': this.state.hotspotList[i].name,
              'coordinates': [parseFloat(this.state.hotspotList[i].lon), parseFloat(this.state.hotspotList[i].lat)]
            });
          }
        }
      }

      charSet = charSet + this.state.hotspotList[i].name;
    }
    var textLayers = new TextLayer({
      id: 'TextLayer',
      data: textData,

      /* props from TextLayer class */
      autoHighlight: false,
      background: true,
      backgroundColor: [255, 255, 255, 150], //cannot use alpha channel
      backgroundPadding: [15, 6, 15, 4],
      characterSet: charSet.split(''),
      getAlignmentBaseline: 'top',
      getAngle: 0,
      getColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        // convert to RGB
        return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
      },
      getPosition: d => d.coordinates,
      getSize: 12,
      getText: d => d.name,
      getTextAnchor: 'middle',
      sizeScale: 1,
      pickable: true,
    });

    return textLayers;
  }

  // Custom picking radius calculation to achieve desired zoom-radius relationships
  calculateZoomBasedPickingRadius() {
    const hotspotRadius = parseInt(this.state.currentHotspot?.radius ?? 0);
    const currentZoom = this.state.viewState?.zoom || this.state.zoom || 17;
    
    
    let multiplier;
    if (currentZoom <= 15) {
      multiplier = 0.5;
    } else {
      multiplier = 1.05 * Math.pow(2, currentZoom - 16);
    }
    
    const baseCalculation =  parseInt(hotspotRadius * multiplier);
    const clampedRadius = Math.max(30, baseCalculation);
    
    //console.log(`[PickingRadius] hotspotRadius:${hotspotRadius}, zoom:${currentZoom}, multiplier:${multiplier.toFixed(2)}, baseCalc:${baseCalculation.toFixed(1)}, final:${clampedRadius}`);
    
    return clampedRadius;
  }

  genEditableHotspotLayer() {
    if (!this.state.isHotspotEditMode || !this.state.editableHotspotData) {
      return null;
    }

    const selectedFeatureIndexes = [0];
    
    const editableLayer = new EditableGeoJsonLayer({
      id: 'editable-hotspot-layer',
      data: this.state.editableHotspotData,
      mode: this.editModes,
      pickingRadius: this.calculateZoomBasedPickingRadius(),
      selectedFeatureIndexes,
      _subLayerProps: {
        guides: {
          pointType: 'circle',
          _subLayerProps: {
            'points-circle': {
              type: ScatterplotLayer,
              radiusScale: 1,
              stroked: true,
              getLineWidth: 3,
              radiusMinPixels: 12,
              radiusMaxPixels: 16,
              getRadius: 6,
              getFillColor: [255, 0, 0, 220],
              getLineColor: [255, 255, 255, 255],
            },
          },
        },
        geojson: {
          // Make the main geojson layer invisible - just for interaction
          getFillColor: [0, 0, 0, 0],
          getLineColor: [0, 0, 0, 0],
          pointRadiusMinPixels: 0,
          pointRadiusMaxPixels: 0,
        },
      },
      onEdit: this.onHotspotEdit
    });

    return editableLayer;
  }

  // Generate a separate circle layer for the hotspot area in edit mode
  genEditableHotspotCircleLayer() {
    if (!this.state.isHotspotEditMode || !this.state.currentHotspot || 
        isUnsetCoord(this.state.currentHotspot.lat) || isUnsetCoord(this.state.currentHotspot.lon)) {
      return null;
    }

    const circleData = [{
      'color': parseInt(this.state.currentHotspot.color).toString(16),
      'radius': parseInt(this.state.currentHotspot.radius),
      'centroid': [parseFloat(this.state.currentHotspot.lon), parseFloat(this.state.currentHotspot.lat)]
    }];

    const circleLayer = new ScatterplotLayer({
      id: 'EditableHotspotCircleLayer',
      data: circleData,
      extruded: false,
      getLineWidth: 2,
      diskResolution: 30,
      elevationScale: 100,
      getElevation: 0.4 * 50,
      
      getLineColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        var rgb = hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0];
        rgb.push(0.8 * 255); // 80% opacity
        return rgb;
      },
      getFillColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        hex = hex + '1f';
        return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
      },
      getPosition: d => d.centroid,
      getRadius: d => d.radius,
      radiusMinPixels: 1,
      stroked: true,
      pickable: false // Don't interfere with editing
    });

    return circleLayer;
  }

  // Generate a center pin marker for the hotspot in edit mode
  genEditableHotspotPinLayer() {
    if (!this.state.isHotspotEditMode || !this.state.currentHotspot ||
        isUnsetCoord(this.state.currentHotspot.lat) || isUnsetCoord(this.state.currentHotspot.lon)) {
      return null;
    }

    const ICON_MAPPING = {
      marker: {
        x: 0,
        y: 0,
        width: 128,
        height: 128,
        anchorY: 128,
        mask: true
      }
    };

    const pinData = [{
      coordinates: [parseFloat(this.state.currentHotspot.lon), parseFloat(this.state.currentHotspot.lat)],
      color: parseInt(this.state.currentHotspot.color).toString(16)
    }];

    const pinLayer = new IconLayer({
      id: 'EditableHotspotPinLayer',
      data: pinData,
      pickable: false,
      autoHighlight: false,
      iconAtlas: 'https://raw.githubusercontent.com/visgl/deck.gl-data/master/website/icon-atlas.png',
      iconMapping: ICON_MAPPING,
      getIcon: d => 'marker',
      sizeScale: 10,
      getPosition: d => d.coordinates,
      getSize: d => 5,
      getColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 255];
      },
    });

    return pinLayer;
  }

  genEditableHotspotTextLayer() {
    if (!this.state.isHotspotEditMode || !this.state.editableHotspotData || !this.state.currentHotspot) {
      return null;
    }

    const textData = [{
      'color': parseInt(this.state.currentHotspot.color).toString(16),
      'name': this.state.currentHotspot.name,
      'coordinates': [parseFloat(this.state.currentHotspot.lon), parseFloat(this.state.currentHotspot.lat)]
    }];

    const charSet = this.state.currentHotspot.name;

    const textLayer = new TextLayer({
      id: 'EditableHotspotTextLayer',
      data: textData,
      autoHighlight: false,
      background: true,
      backgroundColor: [255, 255, 255, 150],
      backgroundPadding: [15, 6, 15, 4],
      characterSet: charSet.split(''),
      getAlignmentBaseline: 'top',
      getAngle: 0,
      getColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
      },
      getPosition: d => d.coordinates,
      getSize: 12,
      getText: d => d.name,
      getTextAnchor: 'middle',
      sizeScale: 1,
      pickable: false, // Don't interfere with hotspot dragging
    });

    return textLayer;
  }

  genCircleLayer(){
    var circleData = []
    for(var i = 0; i<this.state.hotspotList.length; i++){
      // In edit mode, only show non-current hotspots (current one is handled by editable layer)
      if (this.state.isHotspotEditMode && this.state.currentHotspot && 
          this.state.hotspotList[i].id === this.state.currentHotspot.id) {
        continue;
      }

      if(this.state.currentView === VIEW_HOTSPOT_LIST){
        circleData.push({
          'color': parseInt(this.state.hotspotList[i].color).toString(16),
          'radius': parseInt(this.state.hotspotList[i].radius),
          'centroid': [parseFloat(this.state.hotspotList[i].lon), parseFloat(this.state.hotspotList[i].lat)]
        });
      }else{
        if(this.state.hotspotList[i].id == this.state.currentHotspot.id){
          // Skip - handled by editable layer in edit mode
          if (!this.state.isHotspotEditMode) {
            circleData.push({
              'color': parseInt(this.state.hotspotList[i].color).toString(16),
              'radius': parseInt(this.state.hotspotList[i].radius),
              'centroid': [parseFloat(this.state.hotspotList[i].lon), parseFloat(this.state.hotspotList[i].lat)]
            });
          }
        }
      }
    }

    var circleLayers = new ScatterplotLayer({
      id: 'ColumnLayer',
      data: circleData,
      extruded: false,
      getLineWidth: 2,
      diskResolution: 30,
      elevationScale: 100,
      getElevation: 0.4 * 50,
      
      getLineColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        // convert to RGB
        var rgb = hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0];
        // add alpha value
        rgb.push(0.8 * 255); // 80% opacity
        return rgb;


      },
      getFillColor: f => {
        var hex = f.color;
        while(hex.length != 6){
          hex = '0' + hex;
        }
        hex = hex + '1f';
        // convert to RGB
        return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
      },
      getPosition: d => d.centroid,
      getRadius: d => d.radius,
      radiusMinPixels: 1,
      stroked: true,
      pickable: !this.state.isHotspotEditMode // Disable picking in edit mode
    });

    return circleLayers;
  }

  render() {
    const initialViewState = {
      longitude: this.state.currentView === VIEW_HOTSPOT_LIST ? this.state.localeCenter.lng : this.state.hotspotCenter.lng,
      latitude: this.state.currentView === VIEW_HOTSPOT_LIST ? this.state.localeCenter.lat : this.state.hotspotCenter.lat,
      zoom: this.state.zoom
    };

    const { viewState, map } = this.state;
    const boxLayers = this.genBoxData();
    const textLayers = this.genTextLayer();
    const circleLayers = this.genCircleLayer();
    const editableHotspotLayer = this.genEditableHotspotLayer();
    const editableHotspotCircleLayer = this.genEditableHotspotCircleLayer();
    const editableHotspotPinLayer = this.genEditableHotspotPinLayer();
    const editableHotspotTextLayer = this.genEditableHotspotTextLayer();
  
    // Create layers array with proper ordering
    const layers = [boxLayers, circleLayers, textLayers];
    
    // Add editable hotspot layers in correct order
    if (editableHotspotCircleLayer) {
      layers.push(editableHotspotCircleLayer);
    }
    if (editableHotspotPinLayer) {
      layers.push(editableHotspotPinLayer);
    }
    if (editableHotspotLayer) {
      layers.push(editableHotspotLayer);
    }
    if (editableHotspotTextLayer) {
      layers.push(editableHotspotTextLayer);
    }

    return (
      <div style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <HotspotViewerBar root={this.props.root} parent={this} ref="hotspotViewerBar"/>
  
        <SimpleGrid cols={{ base: 1, sm: 4 }} spacing={0} style={{ flex: 1, minHeight: 0, height: this.state.height }}>
          <div style={{ gridColumn: 'span 1', height: this.state.height, overflow: 'auto' }}>
            <Switch condition={this.state.currentView}>
              <Case value={VIEW_HOTSPOT_LIST}>
                <HotspotListView root={this.props.root} parent={this}/>
              </Case>
              <Case value={VIEW_HOTSPOT_DETAIL}>
                {/* Keyed by hotspot: the view loads its content list in the
                    constructor, so switching hotspot must remount it (e.g. the
                    3D-job indicator jumping between hotspots). */}
                <HotspotDetailView key={this.state.currentHotspot ? this.state.currentHotspot.id : 'none'} root={this.props.root} parent={this} currentHotspot={this.state.currentHotspot} ref="hotspotDetailView"/>
              </Case>
            </Switch>
          </div>
          <div style={{ gridColumn: 'span 3', height: this.state.height }}>
            <div style={{position:"relative", height: this.state.height}}>
              <DeckGL
                initialViewState={initialViewState}
                viewState={viewState}
                controller={{
                  doubleClickZoom: false
                }} 
                height={this.state.height}
                layers={layers}
                onViewStateChange={(viewState =>{
                  this.setState({
                    viewState: {
                      ...this.state.viewState,
                      zoom: viewState.viewState.zoom,
                      longitude: viewState.viewState.longitude,
                      latitude: viewState.viewState.latitude,
                      transitionDuration: 0,
                      transitionInterpolator: null
                    }
                  });
  
                  if (this.state.marker_data.show){
                    const viewport = new WebMercatorViewport(viewState.viewState);
                    const ul = viewport.unproject([0, 0]);
                    const br = viewport.unproject([this.state.width, this.state.height], false);
                    this.state.map.zoom = viewState.viewState.zoom;
                    this.state.map.center.lon = viewState.viewState.longitude;
                    this.state.map.center.lat = viewState.viewState.latitude;
                    this.state.map.bounds.ul.lon = ul[0];
                    this.state.map.bounds.ul.lat = ul[1];
                    this.state.map.bounds.br.lon = br[0];
                    this.state.map.bounds.br.lat = br[1];
                    if (this.state.marker_data.lat > this.state.map.bounds.ul.lat || this.state.marker_data.lon < this.state.map.bounds.ul.lon){
                      this.setState({
                        marker_data:{
                          ...this.state.marker_data,
                          out_screen: true
                        }
                      });
                      this.state.marker_data.out_screen = true;
                    }else{
                      this.setState({
                        marker_data:{
                          ...this.state.marker_data,
                          out_screen: false
                        }
                      });
                      this.state.marker_data.out_screen = false;
                    }
                  }
                })}
              >
  
                <Map mapStyle={map.style} mapboxAccessToken={Config.MBOX_KEY()} attributionControl={false}/>
                
                {/* Remove the old marker - now handled by editable layer */}
                
                <Button.Group style={{top:10, left:10, position:"relative"}}>
                  <Button size="xs" variant={this.state.map._style=="mono"? "default":"filled"} onClick={
                    () => {
                      this.setState({
                        map:{
                          ...this.state.map,
                          style: Config.MBOX_STYLE(Localization.strings.getLanguage()).mono,
                          _style: "mono"
                        }
                      });
                      this.state.map.style = Config.MBOX_STYLE(Localization.strings.getLanguage()).mono;
                      this.state.map._style = "mono";
                    }
                  }>{Localization.strings.MAP_MONO}</Button>
                  <Button size="xs" variant={this.state.map._style=="satellite"? "default":"filled"} onClick={
                    () => {
                      this.setState({
                        map:{
                          ...this.state.map,
                          style: Config.MBOX_STYLE(Localization.strings.getLanguage()).satellite,
                          _style: "satellite"
                        }
                      });
                      this.state.map.style = Config.MBOX_STYLE(Localization.strings.getLanguage()).satellite;
                      this.state.map._style = "satellite";
                    }
                  }>{Localization.strings.MAP_SATELLITE}</Button>
                  <Button size="xs" variant={this.state.map._style=="cartoon"? "default":"filled"} onClick={
                    () => {
                      this.setState({
                        map:{
                          ...this.state.map,
                          style: Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon,
                          _style: "cartoon"
                        }
                      });
                      this.state.map.style = Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon;
                      this.state.map._style = "cartoon";
                    }
                  }>{Localization.strings.MAP_VECTOR}</Button>
                </Button.Group>
                <div 
                  style={{
                    position: 'absolute', 
                    top: 50, 
                    left: 10, 
                    zIndex: 1, 
                    width: 'auto', 
                    backgroundColor: 'rgba(255, 255, 255, 0.3)', 
                    borderRadius: '4px',
                    padding: '5px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                  onPointerDown={e => e.stopPropagation()}
                  onPointerMove={e => e.stopPropagation()}
                  onContextMenu={e => e.preventDefault()}
                >
                  <ActionIcon variant="subtle" onClick={this.zoomIn}>
                    <IconPlus size={18} />
                  </ActionIcon>
                  <VerticalZoomSlider
                    value={Math.round(this.state.viewState.zoom)}
                    min={this.state.viewState.minZoom}
                    max={this.state.viewState.maxZoom}
                    step={1}
                    onChange={(value) => {
                      this.setState({
                        viewState: {
                          ...this.state.viewState,
                          zoom: value
                        }
                      });
                    }}
                  />
                  <ActionIcon variant="subtle" onClick={this.zoomOut}>
                    <IconMinus size={18} />
                  </ActionIcon>
                </div>
                <ThreeDJobIndicator
                  epackId={this.props.root.getCurrentLocale() ? this.props.root.getCurrentLocale().id : null}
                  onOpenJob={this.open3DComponentPanel}
                />
              </DeckGL>
            </div>
          </div>
        </SimpleGrid>
      </div>
    )
  }
  
}

export default HotspotViewerPage;
